import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { buildWhatsAppUrl, productWhatsAppMessage, normalizeProduct, slugSegment } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import ProductPlaceholder from "@/components/ProductPlaceholder";

const STATUS_STYLES = {
  "Tersedia": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Terjual": "bg-neutral-100 text-neutral-500 border-neutral-200",
  "Segera Hadir": "bg-[#FFF4E8] text-[#E9840D] border-[#FFD3A2]",
  "Hubungi Kami": "bg-[#F8F8F8] text-[#171717] border-[#E5E5E5]",
};

const CONDITION_STYLES = {
  "Baru": "bg-emerald-600 text-white",
  "Rekondisi": "bg-[#2C5AA0] text-white",
  "Bekas": "bg-neutral-700 text-white",
};

function isRealImage(url) {
  if (!url) return false;
  const u = String(url).trim();
  if (!u) return false;
  if (u.includes("images.unsplash.com")) return false;
  return true;
}

export default function ProductCard({ product }) {
  const { settings } = useSettings();
  const wa = settings?.whatsapp || "";
  const p = normalizeProduct(product);
  const galleryUrl = p.gallery_images?.[0]?.url;
  const src = isRealImage(p.featured_image) ? p.featured_image : (isRealImage(galleryUrl) ? galleryUrl : null);
  const status = p.stock_status || "Hubungi Kami";
  const statusStyle = STATUS_STYLES[status] || STATUS_STYLES["Hubungi Kami"];
  const condStyle = p.condition ? CONDITION_STYLES[p.condition] : null;
  const slugKey = slugSegment(p.slug || p.product_name || "produk");
  const detailPath = p.slug ? `/produk/${p.slug}` : "#";

  return (
    <div className="group card-elev rounded-xl flex flex-col overflow-hidden" data-testid={`product-card-${slugKey}`}>
      <Link to={detailPath} className="block relative aspect-[4/3] overflow-hidden bg-[#F8F8F8]">
        {src ? (
          <img src={src} alt={p.product_name || "Produk"} className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <ProductPlaceholder brand={p.brand} model={p.model} alt={`Foto ${p.product_name || "produk"} belum tersedia`} />
        )}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          <span className={`px-2.5 py-1 text-[10px] font-mono-tech uppercase tracking-wider border rounded ${statusStyle}`} data-testid={`status-${slugKey}`}>{status}</span>
          {p.condition && condStyle && (
            <span className={`px-2.5 py-1 text-[10px] font-mono-tech uppercase tracking-wider rounded ${condStyle}`}>{p.condition}</span>
          )}
        </div>
        {p.brand && (
          <div className="absolute top-3 right-3">
            <span className="px-2 py-1 text-[10px] font-mono-tech uppercase tracking-wider bg-white/95 text-[#171717] border border-[#E5E5E5] rounded font-bold">{p.brand}</span>
          </div>
        )}
      </Link>

      <div className="p-4 flex-1 flex flex-col">
        {p.category && <div className="text-xs font-mono-tech uppercase tracking-wider text-[#737373] mb-1">{p.category}</div>}
        <Link to={detailPath}>
          <h3 className="font-display font-bold text-[#171717] text-lg leading-tight mb-1 hover:text-[#FF9418] transition" data-testid={`product-name-${slugKey}`}>
            {p.product_name || "Produk"}
          </h3>
        </Link>
        {p.model && <div className="text-xs text-[#737373] mb-3 font-mono-tech">Model: {p.model}</div>}
        {p.short_description && <p className="text-sm text-[#555555] leading-relaxed line-clamp-2 mb-4">{p.short_description}</p>}

        <div className="mt-auto flex flex-col gap-2 pt-2">
          <Link to={detailPath}>
            <Button variant="outline" className="w-full h-9 border-[#E5E5E5] hover:border-[#FF9418] hover:bg-[#FFF4E8] hover:text-[#FF9418] text-[#171717] rounded-lg text-xs uppercase tracking-wider font-mono-tech" data-testid={`view-detail-${slugKey}`}>
              Lihat Detail <ArrowRight size={12} />
            </Button>
          </Link>
          {wa && p.product_name && (
            <a href={buildWhatsAppUrl(wa, productWhatsAppMessage(p.product_name))} target="_blank" rel="noopener noreferrer">
              <Button className="w-full h-9 bg-[#FF9418] hover:bg-[#E9840D] text-white rounded-lg text-xs uppercase tracking-wider font-mono-tech font-semibold shadow-sm" data-testid={`ask-price-${slugKey}`}>
                Tanya Harga
              </Button>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
